/**
 * VYV-K1.4 — typed view of the Kairos plugin-seam manifest.
 *
 * The backend (`internal/pluginseam`) exposes a versioned, capability-enforced
 * seam a vertical (Educatio) codes against to discover and operate Kairos
 * capabilities (CRM spine, inbox, telephony, …). Its per-principal manifest is
 * projected into `GET /api/v1/me/capabilities` under the `seam` key, so the
 * shell/vertical reads it from the SAME endpoint that already drives module
 * gating and the federated `remoteRegistry` — one discovery surface, not two.
 *
 * A manifest is INFORMATIVE for surface decisions: it says which Kairos
 * capabilities the current tenant/role may operate right now. The authoritative
 * gate is the backend `Seam.Resolve`, which re-derives authorization on every
 * operate call — a stale manifest can never grant access. Treat `authorized`
 * here purely as "should I render/offer this surface?".
 */

export interface SeamCapabilityDto {
  /** Provider-seam capability name, e.g. `crm.spine`, `inbox.conversations`. */
  capability: string;
  /** Entitlement module key gating it, e.g. `inbox`, `voice`, `core.records`. */
  entitlementKey: string;
  /** Whether the current tenant/role may operate this capability now. */
  authorized: boolean;
}

export interface SeamManifestDto {
  /** Semantic version of the seam contract (backend `pluginseam.SeamVersion`). */
  version: string;
  /** One entry per Kairos capability the seam exposes, in a stable order. */
  capabilities: SeamCapabilityDto[];
}

/**
 * The set of capability names the current principal may operate, per the
 * manifest. A missing/undefined manifest (seam not wired, or an older backend)
 * yields an empty set — the vertical then shows no Kairos-backed surfaces
 * rather than guessing, and the backend still enforces on any direct call.
 */
export function authorizedSeamCapabilities(manifest: SeamManifestDto | undefined | null): Set<string> {
  if (!manifest?.capabilities) return new Set<string>();
  return new Set(manifest.capabilities.filter((c) => c.authorized).map((c) => c.capability));
}

/**
 * Convenience predicate: may the current principal operate `capability`?
 * Fails CLOSED (false) when the manifest is absent — unlike module-nav gating
 * (`useCapabilities.has`, which fails open for UX), operating a Kairos
 * capability from the vertical should never be offered on a guess.
 */
export function seamCapabilityAuthorized(
  manifest: SeamManifestDto | undefined | null,
  capability: string,
): boolean {
  return authorizedSeamCapabilities(manifest).has(capability);
}
